<template>
  <div class="query-section">
    <v-row>
      <v-col cols="12" md="6">
        <v-select
          class="line-input custom-filled"
          validate-on-blur
          v-model="form.queryId"
          :item-value="(query) => query.id"
          :item-text="(query) => query.name"
          :items="queries"
          label="Query"
          dense
          hide-details="auto" />
      </v-col>
      <v-col cols="12" md="6">
        <v-select
          v-model="form.category"
          validate-on-blur
          class="line-input custom-filled"
          :items="categories"
          label="Category"
          :rules="[rules.required]"
          dense
          hide-details="auto" />
      </v-col>
    </v-row>

    <v-row class="mt-4">
      <v-col>
        <v-btn class="btn-primary" @click="loadQuery" :loading="loadingQuery" :disabled="!form.queryId || loadingQuery">
          Load Query Results
        </v-btn>
      </v-col>
    </v-row>

    <v-row v-if="hasLoadedQuery" class="mt-2">
      <v-col>
        <div v-if="queryResults.length === 0" class="no-results">
          <v-icon class="no-results-icon">mdi-database-off-outline</v-icon>
          <p class="no-results-text">No results found</p>
        </div>
        <div v-else class="query-results">
          <div class="results-header">Query Results ({{ queryResults.length }} records)</div>
          <div class="table-container">
            <table class="results-table">
              <thead>
                <tr>
                  <th v-for="header of headers" :key="header">{{ header }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, i) in queryResults" :key="i">
                  <td v-for="header of headers" :key="header">{{ row[header] }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </v-col>
    </v-row>
  </div>
</template>
<script>
import DataService from '../../../../_services/DataService';
import ValidateService from '../../../../_services/ValidateService';
import Utils from '../../../utils';

export default {
  name: 'Query',
  props: ['pageData'],
  components: {},
  data() {
    return {
      categories: ['Marketing', 'Reminders', 'Invitation', 'Welcome', 'Appointment Set'],
      queries: [],
      form: {
        category: '',
        queryId: '',
      },
      hasLoadedQuery: false,
      queryResults: [],
      headers: [],
      rules: ValidateService.validators,
      loadingQuery: false,
      setAlert: Utils.setAlert,
    };
  },
  watch: {
    ['pageData.parent']: {
      handler() {
        this.setupParentRelationship();
      },
      deep: false,
    },
    ['pageData.parent.selectedTemplate']: {
      handler() {
        this.setSelectedTemplate();
      },
      deep: false,
    },
    ['pageData.parent.queries']: {
      handler() {
        this.setQueries();
      },
      deep: false,
    },
  },
  methods: {
    setupParentRelationship() {
      // eslint-disable-next-line
      this.pageData.parent.queryTab = this; // hack
    },
    setSelectedTemplate() {
      const { selectedTemplate } = this.pageData.parent;
      this.form.queryId = selectedTemplate.queryId;

      let { category } = selectedTemplate;
      category = category ?? '';
      this.form.category = category.split(' ').map((a) => a.charAt(0).toUpperCase() + a.slice(1)).join(' ');
    },
    setQueries() {
      this.queries = this.pageData.parent.queries;
    },
    async loadQuery() {
      if (!this.form.queryId) {
        return;
      }
      
      this.loadingQuery = true;
      try {
        const response = await DataService.getClientQueryResult(this.form.queryId);
        if (response.status === 200) {
          this.hasLoadedQuery = true;
          this.queryResults = response.data;
          if (this.queryResults.length) {
            this.headers = Object.keys(this.queryResults[0]);
          }
        } else {
          this.$root.$emit('raise_alert', this.setAlert('fail', 'Failed to load query results'));
        }
      } catch (error) {
        this.$root.$emit('raise_alert', this.setAlert('fail', 'Failed to load query results'));
      } finally {
        this.loadingQuery = false;
      }
    },
  },
  mounted() {
    if (this.pageData?.parent) {
      this.setupParentRelationship();

      if (this.pageData.parent.selectedTemplate) {
        this.setSelectedTemplate();
      }

      if (this.pageData.parent.queries) {
        this.setQueries();
      }
    }
  },
};
</script>

<style lang="scss" scoped>
@import '@/scss/variables.scss';

$no-results-bg-color: #f8fafc;
$no-results-border-color: #e2e8f0;
$no-results-icon-color: #cbd5e0;
$no-results-text-color: #718096;
$results-header-color: #374151;
$table-header-bg-color: #f8fafc;
$table-header-text-color: #374151;
$table-border-color: #e2e8f0;
$table-hover-bg-color: #f8fafc;
$table-cell-text-color: #6b7280;

.query-section {
  padding: 16px 0;
}

.no-results {
  text-align: center;
  padding: 40px 16px;
  background-color: $no-results-bg-color;
  border-radius: 8px;
  border: 1px solid $no-results-border-color;
  
  .no-results-icon {
    font-size: 48px;
    color: $no-results-icon-color;
    margin-bottom: 12px;
  }
  
  .no-results-text {
    color: $no-results-text-color;
    margin: 0;
    font-size: 14px;
  }
}

.query-results {
  .results-header {
    font-size: 14px;
    font-weight: 600;
    color: $results-header-color;
    margin-bottom: 12px;
  }
  
  .table-container {
    overflow-x: auto;
    border: 1px solid $table-border-color;
    border-radius: 8px;
  }
  
  .results-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
    
    thead {
      background-color: $table-header-bg-color;
      
      th {
        padding: 12px 16px;
        text-align: left;
        font-weight: 600;
        color: $table-header-text-color;
        border-bottom: 2px solid $table-border-color;
        white-space: nowrap;
      }
    }
    
    tbody {
      tr {
        transition: background-color 0.2s ease;
        
        &:hover {
          background-color: $table-hover-bg-color;
        }
        
        &:not(:last-child) {
          border-bottom: 1px solid $table-border-color;
        }
      }
      
      td {
        padding: 12px 16px;
        color: $table-cell-text-color;
        white-space: nowrap;
      }
    }
  }
}
</style>